import React from 'react';

import clsx from 'clsx';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'use-debounce';

import type { CheckoutState } from './Checkout';
import { PriceSeat } from './Checkout';
import { CheckoutEstimate } from './CheckoutEstimate';

const useStyles = makeStyles()(theme => ({
  center: {
    justifyContent: 'center',
  },
  footer: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1.25fr',
    marginTop: theme.spacing(1.5),
  },
  section: {
    '&:last-child': {
      borderRight: 'none',
    },
    borderRight: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    padding: theme.spacing(1.5, 2),
  },
}));

interface CheckoutFooterProps {
  creatingCheckoutUrl: boolean;
  handleCreateCheckoutUrl: () => Promise<void>;
  onSeatDecrement: (seatType: Apollo.OrgRole) => void;
  onSeatIncrement: (seatType: Apollo.OrgRole) => void;
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
  state: CheckoutState;
}

export function CheckoutFooter({
  creatingCheckoutUrl,
  handleCreateCheckoutUrl,
  onSeatDecrement,
  onSeatIncrement,
  setState,
  state,
}: CheckoutFooterProps): JSX.Element | null {
  const { classes } = useStyles();
  const [debouncedState] = useDebounce(state, 1000);
  const isDebouncing = debouncedState !== state;

  return (
    <div className={classes.footer}>
      <div className={classes.section}>
        <PriceSeat
          features={[
            'Launch and use workflows',
            'Draft, edit, and sign unlimited documents',
            'Limited AI usage',
          ]}
          label="Basic Seats:"
          value={state.numBasicSeatsPurchased}
          onDecrement={() => onSeatDecrement(Apollo.OrgRole.Basic)}
          onIncrement={() => onSeatIncrement(Apollo.OrgRole.Basic)}
        />
      </div>
      <div className={classes.section}>
        <PriceSeat
          features={[
            'All capabilities of a Basic Seat',
            'Aline Associate and AI Playbooks for drafting, redlining and deep legal research',
            'AI Reports for document analysis',
          ]}
          label="Full Seats:"
          value={state.numFullSeatsPurchased}
          onDecrement={() => onSeatDecrement(Apollo.OrgRole.Full)}
          onIncrement={() => onSeatIncrement(Apollo.OrgRole.Full)}
        />
      </div>
      <div className={clsx(classes.section, classes.center)}>
        <CheckoutEstimate
          creatingCheckoutUrl={creatingCheckoutUrl}
          debouncedState={debouncedState}
          handleCreateCheckoutUrl={handleCreateCheckoutUrl}
          isDebouncing={isDebouncing}
          setState={setState}
          state={state}
        />
      </div>
    </div>
  );
}
